import { useCallback, useEffect, useRef, useState } from 'react';
import { PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

import { createDraftOptionRow } from 'bl/consultation-forms/createDraftOptionRow.bl';
import { getDuplicateOptionIds } from 'bl/consultation-forms/getDuplicateOptionIds.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import S from 'constants/string.const';
import useIsMobileViewport from 'hooks/useIsMobileViewport.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import elementScrollTo from 'utils/dom/elementScrollTo.util';
import { charCounter } from 'utils/strings/charCounter.util';

import {
  MULTI_OPTION_TYPES,
  ORDERED_TYPES,
  QUESTION_LIMITS,
  QUESTION_TYPE_ICON,
  QUESTION_TYPE_LABELS,
  QUESTION_TYPE_OPTION_PLACEHOLDER_KEY,
  QUESTION_TYPE_OPTION_ROW_LABEL_KEY,
} from '../consultationForms.const';

import CN from './AddQuestionModal.module.scss';

const makeBlankState = () => ({
  step: 1,
  questionText: '',
  description: '',
  selectedType: null,
  options: [createDraftOptionRow(), createDraftOptionRow()],
});

const stateFromQuestion = (question) => ({
  step: MULTI_OPTION_TYPES.includes(question.type) ? 2 : 1,
  questionText: question.text ?? '',
  description: question.description ?? '',
  selectedType: question.type ?? null,
  options: (question.options?.length ? question.options : ['', '']).map((text) => createDraftOptionRow(typeof text === 'string' ? text : '')),
});

const OptionInput = ({ optionId, value, rowNumber, selectedType, isDuplicate, isRemoveDisabled, onRemove, onChange }) => {
  const { t } = useTranslation();
  const optCounter = charCounter(value, QUESTION_LIMITS.OPTION_TEXT_MAX, QUESTION_LIMITS.COUNTER_THRESHOLD);
  const inputId = `aqm-option-${optionId}`;
  const rowLabel = t(QUESTION_TYPE_OPTION_ROW_LABEL_KEY[selectedType], { number: rowNumber });
  const placeholder = t(QUESTION_TYPE_OPTION_PLACEHOLDER_KEY[selectedType], { number: rowNumber });

  return (
    <div className={CN.optionRow} data-option-id={optionId}>
      <div className={CN.fieldLabelWrapper}>
        <label className={CN.fieldLabel} htmlFor={inputId}>
          {rowLabel}
        </label>
        <span className={CN.required}>{S.asterisk}</span>
      </div>
      <div className={CN.optionInputRow}>
        <input
          id={inputId}
          className={cn(CN.textInput, CN.optionInput, { [CN.optionInputDuplicate]: isDuplicate })}
          type="text"
          data-testid={`AddQuestionModal__optionInput-${rowNumber}`}
          placeholder={placeholder}
          value={value}
          maxLength={QUESTION_LIMITS.OPTION_TEXT_MAX}
          onChange={(e) => onChange(optionId, e.target.value)}
        />
        <Button
          variant="outlineForce"
          disabled={isRemoveDisabled}
          onClick={() => onRemove(optionId)}
          tag={`AddQuestionModal__removeOption-${rowNumber}`}
          data-testid={`AddQuestionModal__removeOption-${rowNumber}`}
          noMargin
          noVerticalMargin
          noPadding
          className={CN.removeOptionButton}
        >
          <TrashIcon className={CN.removeOptionIcon} />
        </Button>
      </div>
      {optCounter.showCounter && <span className={CN.charCounter}>{t('consultationForms.builder.charsRemaining', { count: optCounter.remaining })}</span>}
    </div>
  );
};

export const AddQuestionModal = ({ isOpen, onClose, onSave, initialQuestion = null }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const isMobile = useIsMobileViewport();

  const [form, setForm] = useState(makeBlankState);
  const optionsListRef = useRef(null);
  const pendingScrollOptionIdRef = useRef(null);

  const updateForm = useCallback((updates) => setForm((prev) => ({ ...prev, ...updates })), [setForm]);
  const goToStep1 = useCallback(() => updateForm({ step: 1 }), [updateForm]);
  const goToStep2 = useCallback(() => updateForm({ step: 2 }), [updateForm]);

  useEffect(() => {
    if (isOpen) {
      setForm(initialQuestion ? stateFromQuestion(initialQuestion) : makeBlankState());
    }
  }, [isOpen, initialQuestion]);

  const { step, questionText, description, selectedType, options } = form;

  const isMultiOption = MULTI_OPTION_TYPES.includes(selectedType);
  const atMaxOptions = options.length >= QUESTION_LIMITS.MAX_OPTIONS_PER_QUESTION;
  const duplicateOptionIds = getDuplicateOptionIds(options);
  const hasDuplicates = duplicateOptionIds.size > 0;

  const textCounter = charCounter(questionText, QUESTION_LIMITS.QUESTION_TEXT_MAX, QUESTION_LIMITS.COUNTER_THRESHOLD);
  const descCounter = charCounter(description, QUESTION_LIMITS.DESCRIPTION_MAX, QUESTION_LIMITS.COUNTER_THRESHOLD);

  const handleAddOption = () => {
    if (atMaxOptions) return;
    const newRow = createDraftOptionRow();
    pendingScrollOptionIdRef.current = newRow.id;
    updateForm({ options: [...options, newRow] });
  };

  useEffect(() => {
    const optionId = pendingScrollOptionIdRef.current;
    if (!optionId || !optionsListRef.current) return;

    pendingScrollOptionIdRef.current = null;
    const row = optionsListRef.current.querySelector(`[data-option-id="${optionId}"]`);
    elementScrollTo({ element: row, behavior: 'smooth', block: 'nearest' });
  }, [options]);

  const handleRemoveOption = (optionId) => {
    if (options.length <= 2) return;
    updateForm({ options: options.filter((row) => row.id !== optionId) });
  };

  const handleOptionChange = (optionId, nextValue) => {
    updateForm({ options: options.map((row) => (row.id === optionId ? { ...row, value: nextValue } : row)) });
  };

  const handleSave = () => {
    const question = {
      id: initialQuestion?.id ?? `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      text: questionText,
      description: description || undefined,
      type: selectedType,
      required: initialQuestion?.required ?? false,
      options: isMultiOption ? options.map((row) => row.value).filter((o) => o.trim()) : [],
    };
    onSave(question);
  };

  const canProceedStep1 = questionText.trim().length > 0 && selectedType !== null;
  const allStep2OptionsFilled = options.every((row) => row.value.trim().length > 0);
  const canSaveMulti = canProceedStep1 && allStep2OptionsFilled;
  const isSaveDisabled = isMultiOption ? !canSaveMulti : !canProceedStep1;

  return (
    <SalonModal mode={isDarkMode && 'dark'} tag="AddQuestionModal" isOpen={isOpen} onClose={onClose} customClass={cn(CN.modalDialog, { [CN.darkMode]: isDarkMode })}>
      <SalonModal.Header>
        <div className={CN.modalHeader}>
          <Button
            variant="icon"
            tag="AddQuestionModal__close"
            data-testid="AddQuestionModal__close"
            className={CN.closeButton}
            onClick={onClose}
            width="auto"
            height="auto"
            noMargin
            noVerticalMargin
            noPadding
          >
            <XMarkIcon className={CN.closeIcon} />
          </Button>
        </div>
      </SalonModal.Header>

      <SalonModal.Body>
        <div className={CN.body}>
          <h2 className={CN.title}>{t('consultationForms.addQuestionModal.title')}</h2>

          <div className={CN.questionsWrapper}>
            <div className={CN.questionWrapper}>
              <label className={CN.fieldLabel} htmlFor="aqm-question-text">
                {t('consultationForms.addQuestionModal.questionLabel')}
                <span className={CN.required}>{S.asterisk}</span>
              </label>
              <div className={CN.fieldWrapper}>
                <input
                  id="aqm-question-text"
                  className={CN.textInput}
                  type="text"
                  data-testid="AddQuestionModal__questionText"
                  placeholder={t('consultationForms.addQuestionModal.questionPlaceholder')}
                  value={questionText}
                  maxLength={QUESTION_LIMITS.QUESTION_TEXT_MAX}
                  onChange={(e) => updateForm({ questionText: e.target.value })}
                />
                {textCounter.showCounter && <span className={CN.charCounter}>{t('consultationForms.builder.charsRemaining', { count: textCounter.remaining })}</span>}
              </div>
            </div>
            <div className={CN.descriptionWrapper}>
              <label className={CN.fieldLabel} htmlFor="aqm-description">
                {t('consultationForms.addQuestionModal.descriptionLabel')}
              </label>
              <div className={CN.fieldWrapper}>
                <textarea
                  id="aqm-description"
                  className={cn(CN.textInput, CN.textarea)}
                  data-testid="AddQuestionModal__description"
                  placeholder={t('consultationForms.addQuestionModal.descriptionPlaceholder')}
                  value={description}
                  maxLength={QUESTION_LIMITS.DESCRIPTION_MAX}
                  rows={2}
                  onChange={(e) => updateForm({ description: e.target.value })}
                />
                {descCounter.showCounter && <span className={CN.charCounter}>{t('consultationForms.builder.charsRemaining', { count: descCounter.remaining })}</span>}
              </div>
            </div>
          </div>

          {step === 1 && (
            <fieldset className={CN.typeWrapper}>
              <legend className={CN.fieldLabel}>{t('consultationForms.addQuestionModal.answerType')}</legend>
              <div className={CN.typeGrid} role="radiogroup">
                {ORDERED_TYPES.map((type) => {
                  const TypeIcon = QUESTION_TYPE_ICON[type];
                  const isSelected = type === selectedType;
                  return (
                    <button
                      key={type}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      data-testid={`AddQuestionModal__typeCard-${type}`}
                      className={cn(CN.typeCard, { [CN.typeCardSelected]: isSelected })}
                      onClick={() => updateForm({ selectedType: type })}
                    >
                      <div className={cn(CN.iconContainer, { [CN.iconContainerSelected]: isSelected })}>
                        <TypeIcon className={cn(CN.icon, { [CN.darkMode]: isDarkMode })} />
                      </div>
                      {t(QUESTION_TYPE_LABELS[type])}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          {step === 2 && isMultiOption && (
            <>
              <div className={CN.optionsHeader}>
                <span className={CN.fieldLabel}>{t('consultationForms.addQuestionModal.answerType')}</span>
                <Button variant="empty" data-testid="AddQuestionModal__backType" className={CN.backTypeButton} onClick={goToStep1} width="auto" height="auto" noMargin noVerticalMargin noPadding>
                  <span className={CN.backTypeButtonText}>{t(QUESTION_TYPE_LABELS[selectedType])}</span> <PencilIcon className={CN.backTypePencilIcon} />
                </Button>
              </div>

              <div className={CN.optionsWrapper}>
                <div ref={optionsListRef} className={CN.optionsList} data-testid="AddQuestionModal__optionsList">
                  {options.map((row, i) => (
                    <OptionInput
                      key={row.id}
                      optionId={row.id}
                      value={row.value}
                      rowNumber={i + 1}
                      selectedType={selectedType}
                      isDuplicate={duplicateOptionIds.has(row.id)}
                      isRemoveDisabled={options.length <= 2}
                      onRemove={handleRemoveOption}
                      onChange={handleOptionChange}
                    />
                  ))}
                </div>

                {hasDuplicates && (
                  <p className={CN.duplicateWarning} data-testid="AddQuestionModal__duplicateWarning">
                    {t('consultationForms.builder.duplicateOptionWarning')}
                  </p>
                )}

                {isMobile && !atMaxOptions && (
                  <div className={CN.mobileAddOptionButtonWrapper}>
                    <Button variant="outlineForce" data-testid="AddQuestionModal__addOption" xSpace onClick={handleAddOption}>
                      {t('consultationForms.addQuestionModal.addOption')}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </SalonModal.Body>

      <SalonModal.Footer>
        {step === 1 && isMultiOption ? (
          <Button variant="primaryForce" onClick={goToStep2} disabled={!canProceedStep1} tag="AddQuestionModal__next" data-testid="AddQuestionModal__next" className={CN.nextButton} noMargin>
            {t('consultationForms.addQuestionModal.next')}
          </Button>
        ) : step === 2 && isMultiOption ? (
          <div className={CN.footerStep2}>
            {(!isMobile || atMaxOptions) && (
              <div className={CN.footerStep2Leading}>
                {atMaxOptions ? (
                  <p className={CN.limitMessage} data-testid="AddQuestionModal__maxOptionsMessage">
                    {t('consultationForms.builder.maxOptionsReached')}
                  </p>
                ) : (
                  <Button variant="outlineForce" data-testid="AddQuestionModal__addOption" xSpace onClick={handleAddOption}>
                    {t('consultationForms.addQuestionModal.addOption')}
                  </Button>
                )}
              </div>
            )}
            <div className={CN.saveButtonWrapper}>
              <Button
                variant="primaryForce"
                width="fluid"
                onClick={handleSave}
                disabled={isSaveDisabled}
                tag="AddQuestionModal__save"
                data-testid="AddQuestionModal__save"
                className={CN.saveButton}
                noMargin
              >
                {t('consultationForms.addQuestionModal.saveQuestion')}
              </Button>
            </div>
          </div>
        ) : (
          <div className={CN.saveButtonWrapper}>
            <Button
              variant="primaryForce"
              width="fluid"
              onClick={handleSave}
              disabled={isSaveDisabled}
              tag="AddQuestionModal__save"
              data-testid="AddQuestionModal__save"
              className={CN.saveButton}
              noMargin
            >
              {t('consultationForms.addQuestionModal.saveQuestion')}
            </Button>
          </div>
        )}
      </SalonModal.Footer>
    </SalonModal>
  );
};
